/**
 * Shared accordion component.
 *
 * Each <AccordionPanel> is independently expandable. The header is a render
 * prop so callers can include their own icons, checkboxes, badges, and
 * chevrons without the component making any assumptions about the content.
 *
 * Usage (uncontrolled):
 *
 *   <AccordionPanel
 *     header={(open) => (
 *       <>
 *         <MyIcon />
 *         <span>Section title</span>
 *         {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
 *       </>
 *     )}
 *   >
 *     <p>Content revealed when open</p>
 *   </AccordionPanel>
 *
 * Usage (controlled):
 *
 *   <AccordionPanel
 *     open={isOpen}
 *     onOpenChange={setIsOpen}
 *     header={(open) => <HeaderContent open={open} />}
 *   >
 *     <Content />
 *   </AccordionPanel>
 */

import { useEffect, useRef, useState } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccordionPanelProps {
  /**
   * Header content. Receives current open state so callers can render a
   * directional chevron or other open/closed indicator.
   */
  header: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  /** Collapsible content shown when the panel is open. */
  children: React.ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Initial open state when uncontrolled. Defaults to false. */
  defaultOpen?: boolean;
  /** Called when the user toggles the panel (controlled and uncontrolled). */
  onOpenChange?: (open: boolean) => void;
  /** Prevent the user from toggling. */
  disabled?: boolean;
  /** Extra styles on the outer wrapper. */
  style?: React.CSSProperties;
  /** Extra styles on the clickable header row. */
  headerStyle?: React.CSSProperties;
  /** Extra styles on the content area. */
  contentStyle?: React.CSSProperties;
  /** Optional id prefix for aria wiring. */
  id?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AccordionPanel({
  header,
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  style,
  headerStyle,
  contentStyle,
  id,
}: AccordionPanelProps): React.JSX.Element {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = isControlled ? controlledOpen! : internalOpen;

  const headerId  = id ? `${id}-header`  : undefined;
  const regionId  = id ? `${id}-region`  : undefined;

  // Smooth height animation via ref-based measurement
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string>(isOpen ? "none" : "0px");
  const [overflow, setOverflow] = useState<"hidden" | "visible">(isOpen ? "visible" : "hidden");

  // Keep maxHeight in sync when controlled open changes externally
  useEffect(() => {
    if (isOpen) {
      const el = contentRef.current;
      if (el) {
        setMaxHeight(`${el.scrollHeight}px`);
        setOverflow("hidden");
        // After transition ends, remove max-height restriction so content
        // can grow (e.g. nested accordions opening)
        const timer = setTimeout(() => {
          setMaxHeight("none");
          setOverflow("visible");
        }, 220);
        return () => clearTimeout(timer);
      } else {
        setMaxHeight("none");
        setOverflow("visible");
      }
    } else {
      // Snapshot height first so transition animates from current height
      const el = contentRef.current;
      if (el) {
        setMaxHeight(`${el.scrollHeight}px`);
        setOverflow("hidden");
        // Force a reflow before setting to 0
        void el.offsetHeight;
        requestAnimationFrame(() => setMaxHeight("0px"));
      } else {
        setMaxHeight("0px");
        setOverflow("hidden");
      }
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    // Don't toggle if a child (e.g. a Checkbox) swallowed the event
    if ((e.target as HTMLElement).closest('[data-accordion-stop]')) return;
    if (disabled) return;
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const resolvedHeader =
    typeof header === "function" ? header(isOpen) : header;

  return (
    <div style={{ ...style }}>
      {/* Header — acts as the toggle trigger */}
      <div
        id={headerId}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-controls={regionId}
        aria-disabled={disabled || undefined}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle(e as unknown as React.MouseEvent);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          ...headerStyle,
        }}
      >
        {resolvedHeader}
      </div>

      {/* Collapsible content */}
      <div
        id={regionId}
        role="region"
        aria-labelledby={headerId}
        ref={contentRef}
        style={{
          maxHeight,
          overflow,
          transition: "max-height 200ms ease",
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}
